import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Chip
} from '@mui/material';
import { Add, Dashboard as DashboardIcon, Settings } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const getRoleBadgeColor = () => {
    switch (user?.role) {
      case 'locate_admin': return 'primary';
      case 'locate_viewer': return 'info';
      case 'novus_user': return 'secondary';
      default: return 'default';
    }
  };

  const getRoleLabel = () => {
    switch (user?.role) {
      case 'locate_admin': return 'Admin';
      case 'locate_viewer': return 'Viewer';
      case 'novus_user': return 'Novus';
      default: return user?.role;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }} 
            onClick={() => navigate('/')}
          >
            🏦 ATM Facility Management
          </Typography>
          
          {user && (
            <>
              {/* Navigation */}
              <Box sx={{ ml: 4, display: 'flex', gap: 1 }}>
                <Button 
                  color="inherit" 
                  startIcon={<DashboardIcon />}
                  onClick={() => navigate('/')}
                  sx={{ 
                    bgcolor: location.pathname === '/' ? 'rgba(255,255,255,0.1)' : 'transparent'
                  }}
                >
                  Dashboard
                </Button>
                
                {isAdmin && (
                  <>
                    <Button 
                      color="inherit" 
                      startIcon={<Add />}
                      onClick={() => navigate('/notices/new')}
                      sx={{ 
                        bgcolor: location.pathname === '/notices/new' ? 'rgba(255,255,255,0.1)' : 'transparent'
                      }}
                    >
                      New Notice
                    </Button>
                    <Button 
                      color="inherit" 
                      startIcon={<Settings />}
                      onClick={() => navigate('/settings')}
                      sx={{ 
                        bgcolor: location.pathname === '/settings' ? 'rgba(255,255,255,0.1)' : 'transparent'
                      }}
                    >
                      Settings
                    </Button>
                  </>
                )}
              </Box>

              {/* User Info */}
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                    {user.first_name} {user.last_name}
                  </Typography>
                  <Chip 
                    label={getRoleLabel()} 
                    size="small" 
                    color={getRoleBadgeColor() as any}
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                </Box>
                <Button color="inherit" onClick={logout} variant="outlined" size="small">
                  Logout
                </Button>
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, bgcolor: '#f5f5f5' }}>
        {children}
      </Box>

      <Box component="footer" sx={{ py: 2, px: 2, mt: 'auto', bgcolor: 'white', textAlign: 'center', borderTop: '1px solid #e0e0e0' }}>
        <Container maxWidth="sm">
          <Typography variant="body2" color="text.secondary">
            © 2025 Locate Technologies Limited | ATM Facility Management Platform
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;

